"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { navLinks, NavLink } from "@/db/navLinks";

interface SidebarLayoutProps {
    children: React.ReactNode;
    navTitle: string;
    sidebarHeader: string;
    onLinkClick?: (title: string) => void;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children, navTitle, sidebarHeader, onLinkClick }) => {
    const router = useRouter();
    const pathname = router.pathname;
    const asPath = router.asPath;
    const [expandedTitle, setExpandedTitle] = React.useState<string | null>(null);

    // Find the navigation link to get its submenu (search recursive)
    const findNavLink = (links: NavLink[], title: string): NavLink | null => {
        for (const link of links) {
            if (link.title === title) return link;
            if (link.submenu) {
                const found = findNavLink(link.submenu, title);
                if (found) return found;
            }
        }
        return null;
    };

    const navLink = findNavLink(navLinks, navTitle);
    const sidebarLinks = navLink?.submenu || [];

    // The sidebar sticks just below the site header. The header's height changes
    // with screen width and when it collapses on scroll, so follow it rather than
    // hard-coding an offset.
    const [stickyTop, setStickyTop] = React.useState(150);
    React.useEffect(() => {
        const header = document.querySelector(".headerstick");
        if (!header) return;
        const update = () => setStickyTop(Math.round(header.getBoundingClientRect().height) + 16);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="about-us-area ptb-40">
            <div className="container">
                <div className="row">
                    <div className="col-lg-4 col-md-12">
                        <div className="about-sidebar" style={{ "--sidebar-top": `${stickyTop}px` } as React.CSSProperties}>
                            <div className="sidebar-header bg-navy text-white p-4 ">
                                <h2 className="text-white mb-0 text-uppercase" style={{ fontSize: '24px', fontWeight: 'bold' }}>{sidebarHeader}</h2>
                            </div>

                            <ul className="sidebar-list list-unstyled border">
                                {sidebarLinks.map((link, index) => {
                                    // Improved isActive logic: active if exact match OR if one of its sub-items is active
                                    const isCurrentPath = pathname === link.href;
                                    const isSubItemActive = link.submenu?.some(sub => (sub.href.includes('?') || sub.href.includes('#')) ? asPath === sub.href : pathname === sub.href);
                                    const isActive = isCurrentPath || isSubItemActive;
                                    const isExpanded = expandedTitle === link.title || isActive;

                                    return (
                                        <li key={index} className={`sidebar-item ${isActive ? 'active' : ''}`}>
                                            <Link
                                                href={link.href}
                                                className={`d-flex justify-content-between align-items-center p-3 text-decoration-none transition-all text-black ${isActive ? 'bg-navy fw-bold' : 'text-navy hover-bg-light'}`}
                                                onClick={(e) => {
                                                    if (link.submenu) {
                                                        // Only prevent default if it's a dummy link
                                                        if (link.href === "#" || !link.href) {
                                                            e.preventDefault();
                                                        }
                                                        setExpandedTitle(expandedTitle === link.title ? null : link.title);
                                                    }
                                                    if (link.href === "#" && onLinkClick) {
                                                        onLinkClick(link.title);
                                                    }
                                                }}
                                            >
                                                <span>{link.title}{link.extraText}</span>

                                            </Link>

                                            {/* Render nested submenu if active and exists */}
                                            {isExpanded && link.submenu && (
                                                <ul className="submenu-list list-unstyled ps-0 pb-2 bg-light bg-opacity-50">
                                                    {link.submenuHeader && (
                                                        <li className="px-4 py-2 text-uppercase fw-bold  text-muted border-bottom mb-2 bg-white">
                                                            {link.submenuHeader}
                                                        </li>
                                                    )}
                                                    {link.submenu.map((sub, subIndex) => {
                                                        const isSubActive = (sub.href.includes('?') || sub.href.includes('#')) ? asPath === sub.href : pathname === sub.href;
                                                        return (
                                                            <li key={subIndex} className={`submenu-item py-1 px-4 ${isSubActive ? 'bg-white border-start' : ''}`}>
                                                                <Link href={sub.href} className="text-decoration-none d-block">
                                                                    <span className={` d-block transition-all subject-list ${isSubActive ? 'text-primary fw-bold' : 'text-navy hover-navy'}`}>
                                                                        {sub.title}
                                                                    </span>
                                                                </Link>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}

                                            {index < sidebarLinks.length - 1 && <hr className="m-0 opacity-10" />}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                    <div className="col-lg-8 col-md-12">
                        <div className="about-content-wrap ps-lg-4">
                            {children}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
        .bg-navy {
          background-color: #196565;
        }
        .text-navy {
          color: #0f3f3f;
        }
        .sidebar-item.active {
          border-left: 4px solid #eec906;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
        .hover-navy:hover {
          color: #196565 !important;
          padding-left: 5px;
        }
        .transition-all {
          transition: all 0.3s ease;
        }

        /* Desktop, where the sidebar sits beside the content: it stays in view
           while the page scrolls. Its title stays put and only the link list
           scrolls when it is taller than the screen. */
        @media (min-width: 992px) {
          .about-sidebar {
            position: sticky;
            top: var(--sidebar-top, 150px);
            display: flex;
            flex-direction: column;
            max-height: calc(100vh - var(--sidebar-top, 150px) - 20px);
            transition: top 0.2s ease;
          }
          .sidebar-header {
            flex-shrink: 0;
          }
          .sidebar-list {
            flex: 1 1 auto;
            min-height: 0;
            margin-bottom: 0;
            overflow-y: auto;
            overscroll-behavior: contain;
            scrollbar-width: thin;
            scrollbar-color: #196565 #f2f0ea;
          }
          .sidebar-list::-webkit-scrollbar {
            width: 8px;
          }
          .sidebar-list::-webkit-scrollbar-track {
            background: #f2f0ea;
          }
          .sidebar-list::-webkit-scrollbar-thumb {
            background: #196565;
            border-radius: 8px;
            border: 2px solid #f2f0ea;
          }
          .sidebar-list::-webkit-scrollbar-thumb:hover {
            background: #0f3f3f;
          }
        }
      `}</style>
        </div>
    );
};

export default SidebarLayout;
